/**
 * Globe 3D engine — globe.gl initialization with textures, atmosphere, orbit controls.
 *
 * Renders the earth as a 3D interactive globe using globe.gl + Three.js.
 * Manages auto-rotation, WebGL context recovery, and resize handling.
 */

import Globe from 'globe.gl';
import type { GlobeInstance } from 'globe.gl';
import { isTauriRuntime } from '@/utils/tauri';

// ── Types ────────────────────────────────────────────────────────────

export interface Globe3DOptions {
  container: HTMLElement;
  textureUrl?: string;
  atmosphereColor?: string;
  atmosphereAltitude?: number;
  autoRotateSpeed?: number;
  idleResumeMs?: number;
  initialView?: { lat: number; lng: number; altitude: number };
}

export interface GlobeView {
  lat: number;
  lng: number;
  altitude: number;
}

// ── Constants ────────────────────────────────────────────────────────

const DEFAULT_ATMOSPHERE_COLOR = '#4466cc';
const DEFAULT_ATMOSPHERE_ALT = 0.18;
const DEFAULT_AUTO_ROTATE_SPEED = 0.3;
const DEFAULT_IDLE_RESUME_MS = 60_000;
const MIN_DISTANCE = 101;
const MAX_DISTANCE = 600;
const ZOOM_SPEED = 1.4;

// ── State ────────────────────────────────────────────────────────────

let globe: GlobeInstance | null = null;
let container: HTMLElement | null = null;
let resizeObserver: ResizeObserver | null = null;
let idleTimer: ReturnType<typeof setTimeout> | null = null;
/** Tracks whether user is interacting — used for idle rotation logic. */
// eslint-disable-next-line prefer-const
let userInteracting = false;
export function isUserInteracting(): boolean { return userInteracting; }

// ── Initialization ───────────────────────────────────────────────────

export function initGlobe3D(options: Globe3DOptions): GlobeInstance {
  if (globe) destroyGlobe3D();

  container = options.container;

  const isMobile = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
  const dpr = window.devicePixelRatio ?? 1;

  globe = new Globe(container, {
    rendererConfig: {
      powerPreference: isMobile ? 'default' : 'high-performance',
      logarithmicDepthBuffer: isMobile,
      antialias: dpr > 1,
    },
  });

  // Globe appearance
  globe
    .globeImageUrl(options.textureUrl ?? '/textures/earth-topo-bathy.jpg')
    .backgroundImageUrl(null)
    .backgroundColor('rgba(0,0,0,0)')
    .atmosphereColor(options.atmosphereColor ?? DEFAULT_ATMOSPHERE_COLOR)
    .atmosphereAltitude(options.atmosphereAltitude ?? DEFAULT_ATMOSPHERE_ALT)
    .showAtmosphere(true);

  // Disable transition animations for instant data updates
  globe.pathTransitionDuration(0).arcsTransitionDuration(0).polygonsTransitionDuration(0);

  // Orbit controls. WM keys autoRotate/damping on !isDesktopRuntime()
  // (the Tauri shell), so the WEB globe auto-rotates in desktop browsers
  // while the packaged desktop app does not.
  const desktopRuntime = isTauriRuntime();
  const controls = globe.controls();
  if (controls) {
    controls.autoRotate = !desktopRuntime;
    controls.autoRotateSpeed = options.autoRotateSpeed ?? DEFAULT_AUTO_ROTATE_SPEED;
    controls.enablePan = false;
    controls.zoomSpeed = ZOOM_SPEED;
    controls.minDistance = MIN_DISTANCE;
    controls.maxDistance = MAX_DISTANCE;
    controls.enableDamping = !desktopRuntime;
    controls.dampingFactor = 0.15;
  }

  // Pause auto-rotate on interaction, resume after idle (only where
  // auto-rotate is on at all)
  if (!desktopRuntime) {
    setupIdleRotation(options.idleResumeMs ?? DEFAULT_IDLE_RESUME_MS);
  }

  // Set initial view
  if (options.initialView) {
    globe.pointOfView(options.initialView, 0);
  }

  // Resize handling
  resizeObserver = new ResizeObserver((entries) => {
    if (!globe || !container) return;
    const { width, height } = entries[0].contentRect;
    if (width > 0 && height > 0) {
      globe.width(width).height(height);
    }
  });
  resizeObserver.observe(container);

  // WebGL context loss recovery
  const canvas = globe.renderer()?.domElement;
  if (canvas) {
    canvas.addEventListener('webglcontextlost', handleContextLost);
    canvas.addEventListener('webglcontextrestored', handleContextRestored);
  }

  return globe;
}

// ── Public API ───────────────────────────────────────────────────────

export function getGlobe3D(): GlobeInstance | null {
  return globe;
}

export function setGlobeView(lat: number, lng: number, altitude: number, transitionMs = 1000): void {
  globe?.pointOfView({ lat, lng, altitude }, transitionMs);
}

export function getGlobeView(): GlobeView | null {
  if (!globe) return null;
  const pov = globe.pointOfView();
  return { lat: pov.lat, lng: pov.lng, altitude: pov.altitude };
}

export function setGlobeTexture(url: string): void {
  globe?.globeImageUrl(url);
}

export function setAtmosphere(enabled: boolean): void {
  globe?.showAtmosphere(enabled);
}

export function setAutoRotate(enabled: boolean): void {
  const controls = globe?.controls();
  if (controls) controls.autoRotate = enabled;
}

export function destroyGlobe3D(): void {
  if (idleTimer) {
    clearTimeout(idleTimer);
    idleTimer = null;
  }

  if (resizeObserver) {
    resizeObserver.disconnect();
    resizeObserver = null;
  }

  if (globe) {
    const canvas = globe.renderer()?.domElement;
    if (canvas) {
      canvas.removeEventListener('webglcontextlost', handleContextLost);
      canvas.removeEventListener('webglcontextrestored', handleContextRestored);
    }

    globe.pauseAnimation();
    globe._destructor();
    globe = null;
  }

  if (container) {
    container.innerHTML = '';
    container = null;
  }

  userInteracting = false;
}

// ── Idle Rotation ────────────────────────────────────────────────────

function setupIdleRotation(resumeMs: number): void {
  if (!globe) return;

  const pauseRotation = () => {
    userInteracting = true;
    const controls = globe?.controls();
    if (controls) controls.autoRotate = false;

    if (idleTimer) clearTimeout(idleTimer);
    idleTimer = setTimeout(() => {
      userInteracting = false;
      const c = globe?.controls();
      if (c) c.autoRotate = true;
    }, resumeMs);
  };

  // Listen on the renderer's canvas for interaction events
  const canvas = globe.renderer()?.domElement;
  if (canvas) {
    canvas.addEventListener('pointerdown', pauseRotation);
    canvas.addEventListener('wheel', pauseRotation);
  }
}

// ── WebGL Context Recovery ───────────────────────────────────────────

function handleContextLost(e: Event): void {
  e.preventDefault();
  console.warn('[Globe3D] WebGL context lost — pausing animation');
  globe?.pauseAnimation();
}

function handleContextRestored(): void {
  console.info('[Globe3D] WebGL context restored — resuming');
  globe?.resumeAnimation();
}
